import { PermissionRole } from '@next-space/fe-api-idl';
import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { SPECIAL_SPACE_ID } from 'src/common/const';
import { request } from 'src/common/request/request';
import { useAutoShowAiPage } from 'src/hooks/ai/use-ai-chat';
import { useFetchBlock } from 'src/hooks/page/use-fetch-block';
import { getPermissions } from 'src/hooks/share/use-permissions';
import { getCurrentSpaceId } from 'src/hooks/space/get-space';
import { useCurrentSpace, useCurrentSpaceView } from 'src/hooks/space/use-current-space';
import { switchSpaceViewIdBySpaceId } from 'src/hooks/user/switch-space-view-id';
import { useCurrentUser } from 'src/hooks/user/use-current-user';
import { cache } from 'src/redux/store';
import { SearchParams, ViewPath } from 'src/utils';
import { judgeSharePage, useCheckChatPage, useGetPageId } from 'src/utils/getPageId';
import { getTocIds } from './aside/toc/use-toc-id';

export const useRedirect = () => {
  const isSharePage = judgeSharePage();
  const history = useHistory();
  const currentUser = useCurrentUser();
  const currentSpace = useCurrentSpace();
  const currentSpaceView = useCurrentSpaceView();
  const fetchBlock = useFetchBlock();
  const onceAnyDay = useAutoShowAiPage();
  const pageId = useGetPageId();
  const isChatPage = useCheckChatPage();

  return useCallback(async () => {
    if (!currentUser.uuid || !currentSpace.uuid || pageId || location.pathname !== ViewPath.main) {
      return;
    }
    const query = new URLSearchParams(location.search);
    const spaceId = query.get(SearchParams.spaceId);

    if (spaceId !== SPECIAL_SPACE_ID) {
      if (spaceId && spaceId !== currentSpace.uuid) {
        query.delete(SearchParams.spaceId);
        switchSpaceViewIdBySpaceId(spaceId, true);

        location.reload();
        return;
      }
    }

    if (isChatPage) return;
    if (!isSharePage) {
      if (onceAnyDay.showOnce && __BUILD_IN__) {
        onceAnyDay.close();
        history.replace(ViewPath.chat);
        return;
      }
      const { results } = await request.editor.querySpaceVisitsHistory(
        spaceId || currentSpace.uuid,
        0
      );
      const lastViewPage = results?.[0];
      if (lastViewPage?.uuid) {
        const res = await fetchBlock(lastViewPage.uuid);
        if (res.code === 200) {
          const cur = res.data.blocks[lastViewPage.uuid];
          if (cur?.spaceId === getCurrentSpaceId()) {
            const { roleWithoutPublic } = getPermissions(lastViewPage.uuid);
            const checkRole = [PermissionRole.EDITOR, PermissionRole.WRITER];
            if (!checkRole.includes(roleWithoutPublic)) {
              return;
            }

            history.replace(`/${lastViewPage.uuid}?${query.toString()}`);
            return;
          }
        }
      }
    }

    const tocIds = getTocIds(
      spaceId || currentSpace.uuid,
      currentUser.uuid,
      cache.blocks,
      currentSpaceView
    );
    // 跳转顺序是  团队/个人/共享/subPages第一个
    if (tocIds.team.length > 0) {
      history.replace(`/${tocIds.team[0]}?${query}`);
    } else if (tocIds.person.length > 0) {
      history.replace(`/${tocIds.person[0]}?${query}`);
    } else if (tocIds.share.length > 0) {
      history.replace(`/${tocIds.share[0]}?${query}`);
    }
  }, [
    currentSpace.uuid,
    currentSpaceView,
    currentUser.uuid,
    fetchBlock,
    history,
    isChatPage,
    isSharePage,
    onceAnyDay,
    pageId,
  ]);
};
